<template>
  <div class="collapsible-tree-sites" :class="{ collapsed: !visible }">
    <div class="tree-collapse-btn" @click="$emit('toggle')">
      <i :class="visible ? 'el-icon-arrow-left' : 'el-icon-arrow-right'"></i>
    </div>

    <template v-if="visible">
      <!-- 过滤输入框 -->
      <div class="filter-input">
        <el-input
          v-model="filterText"
          placeholder="输入关键字过滤"
          clearable
          @input="onFilterInput"
        />
      </div>

      <!-- 组织树 -->
      <div class="tree-section">
        <div class="tree-section-title">组织</div>
        <div class="tree-content-scroll">
          <el-tree
            ref="orgTree"
            :data="treeData"
            :props="defaultProps"
            default-expand-all
            highlight-current
            :filter-node-method="filterNode"
            @node-click="handleNodeClick"
          />
        </div>
      </div>

      <!-- 监测点位列表 -->
      <div class="site-section" v-show="siteList.length">
        <div class="tree-section-title">监测点位</div>
        <div class="site-list-scroll">
          <div
            v-for="(item, index) in filteredSiteList"
            :key="item.id"
            class="site-item"
            :class="{ active: nowItem === index }"
            @click="handleSiteClick(item, index)"
          >
            {{ item.siteName }}
          </div>
          <div v-if="!filteredSiteList.length" class="site-empty">无匹配点位</div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import Axios from "@/utils/Axios";
import { filterTreeByKeyword } from "@/scripts/treeFilter";

export default {
  name: "CollapsibleTreeWithSites",
  props: {
    visible: { type: Boolean, default: true },
    defaultProps: {
      type: Object,
      default: () => ({ children: "children", label: "deptName" }),
    },
  },
  emits: ["toggle", "node-click", "site-click"],
  data() {
    return {
      filterText: "",
      originalTreeData: [],
      treeData: [],
      loading: false,
      siteList: [],
      nowItem: 0,
      currentDeptId: null,
    };
  },
  computed: {
    filteredSiteList() {
      if (!this.filterText) return this.siteList;
      return this.siteList.filter((s) =>
        s.siteName.toLowerCase().includes(this.filterText.toLowerCase())
      );
    },
  },
  watch: {
    filterText(val) {
      this.treeData = filterTreeByKeyword(this.originalTreeData, val);
    },
  },
  mounted() {
    this.getTree();
  },
  methods: {
    async getTree() {
      this.loading = true;
      try {
        const { data } = await Axios.ajax({
          method: "get",
          url: "/monitor/init",
          params: { ignoreEmptyRegion: true },
        });
        this.originalTreeData = data && data.deptTree ? [data.deptTree] : [];
        this.treeData = this.originalTreeData;
      } catch {
        this.originalTreeData = [];
        this.treeData = [];
      } finally {
        this.loading = false;
      }
    },
    filterNode(value, data) {
      if (!value) return true;
      return data.deptName.toLowerCase().includes(value.toLowerCase());
    },
    onFilterInput() {
      // 输入时自动过滤 — 由 watch 处理树过滤，computed 处理点位过滤
    },
    handleNodeClick(node) {
      this.currentDeptId = node.deptId;
      this.$emit("node-click", node);
      this.loadSites(node.deptId);
    },
    loadSites(deptId) {
      if (!deptId) return;
      Axios.ajax({
        method: "get",
        url: "/monitor/queryList",
        params: { pageNum: 1, showNum: 100, deptId },
      }).then((res) => {
        if (res.data) {
          this.siteList = res.data.dataList || [];
          this.nowItem = 0;
          if (this.siteList.length) {
            this.$emit("site-click", this.siteList[0], 0);
          }
        }
      });
    },
    handleSiteClick(item, index) {
      this.nowItem = index;
      this.$emit("site-click", item, index);
    },
    /** 外部调用：设置当前选中节点 */
    setCurrentKey(key) {
      this.$nextTick(() => {
        if (this.$refs.orgTree) {
          this.$refs.orgTree.setCurrentKey(key);
        }
      });
    },
    /** 外部调用：获取站点列表 */
    getSiteList() {
      return this.siteList;
    },
  },
};
</script>

<style lang="less" scoped>
.collapsible-tree-sites {
  width: 260px;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
  transition: width 0.25s ease;

  &.collapsed {
    width: 36px;
    min-width: 36px;
    overflow: hidden;
    align-items: center;
  }

  .tree-collapse-btn {
    flex-shrink: 0;
    width: 22px;
    height: 22px;
    border-radius: 4px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    color: #909399;
    background: #f0f2f5;
    transition: all 0.2s;
    margin: 8px 8px 0 auto;
    &:hover { background: #e0e4ea; color: #2a62ff; }
  }

  .filter-input {
    flex-shrink: 0;
    padding: 8px 8px 0;
  }

  .tree-section-title {
    font-size: 12px;
    color: #909399;
    padding: 8px 8px 4px;
    flex-shrink: 0;
  }

  .tree-section {
    flex-shrink: 0;
    max-height: 45%;
    display: flex;
    flex-direction: column;
    min-height: 0;

    .tree-content-scroll {
      flex: 1;
      overflow: auto;
      padding: 0 4px;

      :deep(.el-tree) {
        overflow-x: auto;
        width: max-content;
        min-width: 100%;
      }
      :deep(.el-tree-node__content) {
        height: 28px;
      }
    }
  }

  .site-section {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    border-top: 1px solid #f0f0f0;

    .site-list-scroll {
      flex: 1;
      overflow: auto;
      padding: 0 4px 4px;
    }

    .site-item {
      cursor: pointer;
      height: 32px;
      padding: 0 8px;
      line-height: 32px;
      font-size: 13px;
      color: #7d879f;
      border-radius: 4px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      &:hover { color: #2a62ff; background: rgba(42, 98, 255, 0.04); }
      &.active { color: #2a62ff; background: rgba(42, 98, 255, 0.08); font-weight: 500; }
    }

    .site-empty {
      padding: 16px 8px;
      text-align: center;
      color: #c0c4cc;
      font-size: 12px;
    }
  }
}
</style>
